import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  recordsPerPage: number;
  totalRecords: number;
  onRecordsPerPageChange: (recordsPerPage: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  recordsPerPage,
  totalRecords,
  onRecordsPerPageChange
}) => {
  const getVisiblePages = (): number[] => {
    const pages: number[] = [];
    const maxVisiblePages: number = 5;
    
    let startPage: number = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage: number = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const startRecord: number = (currentPage - 1) * recordsPerPage + 1;
  const endRecord: number = Math.min(currentPage * recordsPerPage, totalRecords);

  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onRecordsPerPageChange(Number(e.target.value));
  };

  const handlePageChange = (page: number): void => {
    onPageChange(page);
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 bg-white border-t">
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Showing</span>
          <select
            value={recordsPerPage}
            onChange={handleRecordsPerPageChange}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <span className="text-sm text-gray-600">
          Showing {startRecord} to {endRecord} out of {totalRecords} records
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
        >
          <ChevronLeft size={16} />
        </button>
        
        {getVisiblePages().map((page: number) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-1 rounded ${
              page === currentPage
                ? 'bg-blue-500 text-white'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            type="button"
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          type="button"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;